// src/components/Help.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  FaBook,
  FaBullhorn,
  FaCalendarAlt,
  FaChartPie,
  FaChevronLeft,
  FaChevronRight,
  FaCheck,
  FaFilePdf,
  FaGlobe,
  FaHandsHelping,
  FaHeadphones,
  FaHeart,
  FaHome,
  FaKeyboard,
  FaLightbulb,
  FaListUl,
  FaMicrophoneAlt,
  FaMusic,
  FaPause,
  FaPlay,
  FaPlayCircle,
  FaPrayingHands,
  FaQuestionCircle,
  FaShareAlt,
  FaTv,
  FaUsers,
  FaVideo,
  FaWaveSquare,
  FaShoppingCart
} from "react-icons/fa";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import "./Help.css";
import SEO from "./SEO";
import { SHORTCUT_LEGEND } from "../navigationShortcuts";

const slides = [
  {
    id: "hero",
    accent: "https://globalchristiancommunity.com/",
    title: "Global Christian Community",
    subtitle: "“Uniting believers worldwide through worship, Scripture, and prayer”",
    description:
      "A ministry of GlobalChristianCommunity.com — designed so worship, Scripture, prayer, and community are simple to access. Use the home tiles or left navigation to open Worship, Bible, Videos, Audio, GCC TV, Present, Session, DJ Mixer, Music, Events, Store, Songs, Karaoke, Messages, Help, or Join Us.",
    icon: FaHandsHelping,
    details: [
      "Every module is simple so worship and Scripture stay front-and-center.",
      "Home tiles, shortcuts, and the menu keep tools one click away.",
    ],
    accentColor: "#1d4ed8",
  },
  {
    id: "mission",
    accent: "Mission",
    title: "Mission",
    subtitle: "Equip every believer, family, church, and small group",
    description:
      "To equip believers, churches, families, and small groups with easy digital tools for worship, Bible study, prayer, and spiritual growth.",
    icon: FaPrayingHands,
    details: [
      "Tools intentionally built for volunteers, leaders, and families who want simplicity.",
      "Focus on worship, Scripture, and prayer without distraction.",
    ],
    accentColor: "#0ea5e9",
  },
  {
    id: "vision",
    accent: "Vision",
    title: "Vision",
    subtitle: "A global, multilingual community rooted in faith and truth",
    description:
      "A worldwide fellowship where worship is accessible, Scripture is alive, and technology supports rather than distracts.",
    icon: FaGlobe,
    details: [
      "Multilingual experiences covering English, Tamil, Malayalam, and more.",
      "A global fellowship uniting believers across geography and time zones.",
    ],
    accentColor: "#a855f7",
  },
  {
    id: "home",
    accent: "Home Hub",
    title: "Home Tile",
    subtitle: "Launch every experience without hunting menus",
    description:
      "The home page displays large tiles for every module — Worship, Bible, Videos, Audio, GCC TV, Present, Session, DJ Mixer, Music, Events, Store, Songs, Karaoke, Messages, Help, and Join Us.",
    icon: FaHome,
    details: [
      "Tap or click any tile to open a module.",
      "Designed for clear navigation on phones and desktops.",
    ],
    accentColor: "#0ea5e9",
  },
  {
    id: "shortcuts",
    accent: "Quick Access",
    title: "Keyboard Shortcuts",
    subtitle: "Ctrl/Cmd + key from anywhere",
    description:
      "Jump straight to Worship, Bible, Video, Audio, Present, Session, GCC TV, Music, and more with one gesture — no menu hunting required.",
    icon: FaKeyboard,
    shortcuts: SHORTCUT_LEGEND,
    accentColor: "#fbbf24",
  },
  {
    id: "page-shortcuts",
    accent: "Page Shortcuts",
    title: "Page Shortcuts",
    subtitle: "Page-specific keyboard controls",
    description:
      "Compact cheat sheet for the keys each page listens to. Keep this nearby when you present or lead.",
    icon: FaKeyboard,
    pageShortcutGroups: [
      {
        page: "Home",
        shortcuts: [
          { keys: "Control", description: "Hide/show the home UI" },
          { keys: "Arrow Left / Arrow Right", description: "Previous/next hero video" },
          { keys: "Arrow Up / Arrow Down", description: "Cycle hero playlists" },
          { keys: "Space", description: "Play/pause hero video" },
          { keys: "J", description: "Open Join/Subscribe" },
          { keys: "T", description: "Open GCC TV" },
        ],
      },
      {
        page: "Worship",
        shortcuts: [
          { keys: "Arrow Right / Page Down", description: "Next content" },
          { keys: "Arrow Left / Page Up", description: "Previous content" },
          { keys: "Arrow Up", description: "Previous song" },
          { keys: "Arrow Down", description: "Next song" },
          { keys: "Space", description: "Play/pause" },
          { keys: "G", description: "Show all tiles (back)" },
          { keys: "Control / H", description: "Hide/show controls" },
          { keys: "> / .", description: "Increase playback rate" },
          { keys: "< / ,", description: "Decrease playback rate" },
          { keys: "1-9", description: "Select YouTube link" },
          { keys: "S", description: "Slide display mode" },
          { keys: "K", description: "Camera + slides display mode" },
          { keys: "R", description: "Camera + present display mode" },
          { keys: "J", description: "Camera + video display mode" },
          { keys: "F", description: "Entire/full display mode" },
          { keys: "V", description: "Video display mode" },
          { keys: "M", description: "Music display mode" },
          { keys: "P", description: "Presenter display mode" },
          { keys: "N", description: "Newsletter display mode" },
          { keys: "= / +", description: "Increase font size" },
          { keys: "- / _", description: "Decrease font size" },
          { keys: "] / }", description: "Increase lines or columns" },
          { keys: "[ / {", description: "Decrease lines or columns" },
          { keys: "?", description: "Show keyboard shortcuts" },
          { keys: "*", description: "Increase auto-advance seconds" },
          { keys: "/", description: "Decrease auto-advance seconds" },
          { keys: "T", description: "Focus display mode dropdown" },
          { keys: "O", description: "Focus catalog dropdown" },
          { keys: "B", description: "Focus category dropdown" },
          { keys: "I", description: "Focus song dropdown" },
          { keys: "E", description: "Focus search box" },
          { keys: "D", description: "Focus slide content" },
        ],
      },
      {
        page: "Bible",
        shortcuts: [
          { keys: "F1", description: "Open help panel" },
          { keys: "Control", description: "Toggle top and bottom controls" },
          { keys: "Space", description: "Play/pause reading or Bible.is media" },
          { keys: "Arrow Left / Arrow Right", description: "Previous/next slide" },
          { keys: "Arrow Up / Arrow Down", description: "Previous/next chapter" },
          { keys: "Ctrl/Cmd + Alt/Shift + D", description: "Cycle display modes" },
          { keys: "Ctrl/Cmd + Alt/Shift + H", description: "Cycle highlight modes" },
          { keys: "Ctrl/Cmd + Alt/Shift + L", description: "Cycle languages" },
          { keys: "Ctrl/Cmd + Alt/Shift + G", description: "Cycle translations" },
          { keys: "Ctrl/Cmd + Alt/Shift + F", description: "Focus search bar" },
          { keys: "Ctrl/Cmd + Alt/Shift + B", description: "Focus book selector" },
          { keys: "Ctrl/Cmd + Alt/Shift + J", description: "Focus chapter selector" },
          { keys: "Ctrl/Cmd + Alt/Shift + V", description: "Toggle voice narration" },
          { keys: "Ctrl/Cmd + Alt/Shift + C", description: "Toggle secondary translation" },
          { keys: "Ctrl/Cmd + Alt/Shift + T", description: "Toggle controls" },
          { keys: "Ctrl/Cmd + Alt/Shift + M", description: "Cycle Bible.is modes" },
          { keys: "Ctrl/Cmd + Alt/Shift + ] / [", description: "Verses per slide" },
          { keys: "Ctrl/Cmd + Alt/Shift + . / ,", description: "Font size up/down" },
          { keys: "Ctrl/Cmd + Alt/Shift + S", description: "Copy share link" },
          { keys: "Tab", description: "Cycle matching book codes" },
          { keys: "Enter", description: "Load quick jump passage" },
          { keys: "Backspace", description: "Step back quick jump input" },
          { keys: "Escape", description: "Clear quick jump buffer" },
        ],
      },
      {
        page: "Present",
        shortcuts: [
          { keys: "Arrow Left / Arrow Right", description: "Previous/next slide" },
          { keys: "Space", description: "Play/pause autoplay" },
          { keys: "Shift + Tab", description: "Focus controls" },
          { keys: "<", description: "Slow down autoplay" },
          { keys: ">", description: "Speed up autoplay" },
          { keys: "Control", description: "Toggle controls" },
          { keys: "Escape", description: "Close presentation" },
        ],
      },
      {
        page: "Session",
        shortcuts: [
          { keys: "Space", description: "Play/pause auto-advance" },
          { keys: "Arrow Right / Arrow Left", description: "Advance within item" },
          { keys: "Arrow Down / Arrow Up", description: "Jump between session items" },
          { keys: "Option (⌥) or O", description: "Show/hide session console" },
          { keys: "Control (⌃)", description: "Show/hide navigation controls" },
          { keys: "T", description: "Toggle slide timer" },
          { keys: "?", description: "Show/hide shortcuts guide" },
        ],
      },
      {
        page: "GCC TV",
        shortcuts: [
          { keys: "Control", description: "Show/hide controls" },
          { keys: "Arrow Right / F9", description: "Next video" },
          { keys: "Arrow Left / F7", description: "Previous video" },
          { keys: "Arrow Up / Arrow Down", description: "Switch playlists" },
          { keys: "< / ,", description: "Decrease playback rate" },
          { keys: "> / .", description: "Increase playback rate" },
          { keys: "Escape", description: "Clear typeahead search" },
          { keys: "Type letters", description: "Typeahead search videos" },
        ],
      },
      {
        page: "Songs",
        shortcuts: [
          { keys: "Control", description: "Toggle the playlist drawer" },
        ],
      },
      {
        page: "Messages",
        shortcuts: [
          { keys: "Control", description: "Toggle the playlist drawer" },
        ],
      },
      {
        page: "Help",
        shortcuts: [
          { keys: "Arrow Left / Arrow Right", description: "Previous/next slide" },
          { keys: "Space", description: "Play/pause the carousel" },
          { keys: "Control", description: "Show/hide controls" },
          { keys: "Escape", description: "Show/hide controls" },
        ],
      },
    ],
    accentColor: "#fbbf24",
  },
  {
    id: "worship",
    accent: "Worship Module",
    title: "Worship Module",
    subtitle: "Show lyrics with music or karaoke",
    description:
      "Browse worship songs and display lyrics on-screen with background music or karaoke video.",
    icon: FaPrayingHands,
    details: [
      "Search or scroll to find worship songs.",
      "Tap a song bubble to play lyrics with audio/video behind it.",
    ],
    accentColor: "#f97316",
  },
  {
    id: "bible",
    accent: "Bible Module",
    title: "Bible Explorer",
    subtitle: "Scripture at your fingertips",
    description:
      "Select a book and chapter then read, listen to, or watch Bible passages. Choose translations and audio/video formats.",
    icon: FaBook,
    details: [
      "Choose book → chapter → verse quickly.",
      "Audio or video playback available for Scripture reading.",
    ],
    accentColor: "#0ea5e9",
  },
  {
    id: "videos",
    accent: "Video Explorer",
    title: "Video Explorer",
    subtitle: "Curated GCC YouTube library",
    description:
      "Browse curated playlists of worship songs, teachings, and Bible readings from the Global Christian Community YouTube channel. The player keeps queue and controls visible.",
    icon: FaPlayCircle,
    details: [
      "Use search and filters to find videos.",
      "Player shows current video, queue, and playback controls.",
    ],
    accentColor: "#dc2626",
  },
  {
    id: "audio",
    accent: "Audio Explorer",
    title: "Audio Explorer",
    subtitle: "Music and messages in audio",
    description:
      "Listen to playlists of music, teachings, or Scripture in a music-style player. Includes queue, play/pause, shuffle, and volume control.",
    icon: FaHeadphones,
    details: [
      "Search audio playlists or tracks.",
      "Queue and playback controls are easy to use.",
    ],
    accentColor: "#6366f1",
  },
  {
    id: "present",
    accent: "Present Module",
    title: "Present / Infographics",
    subtitle: "Visual slide content for teaching",
    description:
      "View presentation slides for Bible chapters, themes, and teaching visuals with summaries.",
    icon: FaChartPie,
    details: [
      "Tap a Bible book or topic to open visuals.",
      "Help for teaching or study settings.",
    ],
    accentColor: "#7c3aed",
  },
  {
    id: "session",
    accent: "Session Module",
    title: "Session Module",
    subtitle: "Build entire worship sessions",
    description:
      "Create complete worship sessions with songs, readings, messages, slides, and external video — all accessible from one link and simple controls.",
    icon: FaListUl,
    details: [
      "Drag and drop items from the library into your session outline.",
      "Share a single session link for exact playback sequence.",
    ],
    accentColor: "#eab308",
  },
  {
    id: "dj",
    accent: "DJ Mixer",
    title: "DJ Mixer (Beta)",
    subtitle: "Mix audio and atmosphere tracks",
    description:
      "Mix audio tracks with dual decks, BPM control, loops, and FX using curated playlists.",
    icon: FaWaveSquare,
    details: [
      "Deck One & Two have independent play and BPM controls.",
      "Use loops, effects, and filters for creative mixes.",
    ],
    accentColor: "#f43f5e",
  },
  {
    id: "music",
    accent: "Music Atmosphere",
    title: "Music Atmosphere",
    subtitle: "Background music and worship atmospheres",
    description:
      "Choose background worship music playlists to play as atmosphere during prayer, meditation, or gatherings.",
    icon: FaMusic,
    details: [
      "Search and select tracks or playlists.",
      "Play, pause, next/prev, and stop controls available.",
    ],
    accentColor: "#0f766e",
  },
  {
    id: "live",
    accent: "GCC TV",
    title: "GCC TV Module",
    subtitle: "Continuous video stream",
    description:
      "Watch a continuous lineup of videos from the Global Christian Community channel — perfect for lobbies, homes, and fellowship spaces.",
    icon: FaTv,
    details: [
      "Auto-plays playlists one after another.",
      "Playback controls stay visible.",
    ],
    accentColor: "#0ea5e9",
  },
  {
    id: "events",
    accent: "Events Module",
    title: "Events",
    subtitle: "Calendar of gatherings",
    description:
      "View upcoming worship nights, meetings, and community gatherings in calendar view. Navigate months and click dates for details.",
    icon: FaCalendarAlt,
    details: [
      "Google Calendar embed shows events by date.",
      "Use arrows to move through the calendar.",
    ],
    accentColor: "#7c3aed",
  },
  {
    id: "store",
    accent: "Store",
    title: "Store",
    subtitle: "Support the ministry & subscribe",
    description:
      "Get books, recommended titles, or join membership tiers to support the ministry and unlock perks like badges, early access, members-only content, and more.",
    icon: FaShoppingCart,
    details: [
      "Membership tiers (Bronze, Silver, Gold) show perks and prices.",
      "Buttons link to subscribe or join membership.",
    ],
    accentColor: "#16a34a",
  },
  {
    id: "songs",
    accent: "Songs Module",
    title: "Songs",
    subtitle: "Curated song playlists",
    description:
      "Browse song playlists from our YouTube channel in categories like Tamil, English, Malayalam, Bible Melodies, and more. Tap a list to play with lyrics and controls.",
    icon: FaMusic,
    details: [
      "Left playlists panel shows song categories.",
      "Play songs in sequence with visible controls.",
    ],
    accentColor: "#db2777",
  },
  {
    id: "karaoke",
    accent: "Karaoke Module",
    title: "Karaoke",
    subtitle: "Sing with on-screen lyrics",
    description:
      "Select Tamil, English, or Malayalam karaoke playlists and sing along with synchronized on-screen lyrics.",
    icon: FaMicrophoneAlt,
    details: [
      "Left panel lists karaoke playlists.",
      "Lyrics display full-screen with simple controls.",
    ],
    accentColor: "#f43f5e",
  },
  {
    id: "messages",
    accent: "Messages Module",
    title: "Messages",
    subtitle: "Curated Bible teachings",
    description:
      "Browse playlists of Bible messages, teaching highlights, and devotionals from the GCC channel. Tap a playlist to play videos with titles and descriptions.",
    icon: FaBullhorn,
    details: [
      "Side panel lists message categories.",
      "Play messages with visible controls.",
    ],
    accentColor: "#0ea5e9",
  },
  {
    id: "join",
    accent: "Join Module",
    title: "Join & Stay Connected",
    subtitle: "Subscribe on YouTube",
    description:
      "Click to join and subscribe to the Global Christian Community YouTube channel and plug into worship, teaching, and prayer across cultures.",
    icon: FaUsers,
    details: [
      "Single click takes you to the subscribe confirmation.",
      "Grow with the worldwide family.",
    ],
    links: [
      {
        label: "Subscribe on YouTube",
        url: "https://www.youtube.com/@gcc-worship?sub_confirmation=1",
      },
    ],
    accentColor: "#22d3ee",
  },
  {
    id: "help-page",
    accent: "Help Module",
    title: "Help Page",
    subtitle: "Slideshow + controls",
    description:
      "This carousel shows help content — autoplay rotates through modules, and controls keep the right card in view.",
    icon: FaQuestionCircle,
    details: [
      "Autoplay moves every few seconds and play/pause freezes the story.",
      "Use the on-screen arrows or dots to jump between slides.",
    ],
    accentColor: "#0ea5e9",
  },
  {
    id: "closing",
    accent: "Closing & Commitment",
    title: "Closing & Commitment",
    subtitle: "Prepare. Worship. Teach. Pray. Grow.",
    description:
      "Let this carousel keep you rooted in worship, Scripture, and community every time you return.",
    icon: FaHeart,
    details: [
      "Each slide reflects tools you can open from the home tile or left nav.",
      "Bring these modules into your home, group, or volunteer teams today.",
    ],
    accentColor: "#be185d",
  },
];

const EXPORT_WIDTH = 1600;
const EXPORT_HEIGHT = 900;

const buildCalloutCopy = (slide) => {
  const explicit = slide.callouts ?? [];
  const fallback = [];
  if (slide.subtitle) {
    fallback.push(slide.subtitle);
  }
  if (slide.details) {
    fallback.push(...slide.details.slice(0, 2));
  }
  return Array.from(new Set([...explicit, ...fallback]));
};

const buildDetailList = (slide, calloutCopy) => {
  if (!slide.details) return [];
  const calloutSet = new Set(calloutCopy);
  return slide.details.filter((detail) => !calloutSet.has(detail));
};

function Help() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [controlsVisible, setControlsVisible] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const exportRootRef = useRef(null);

  const slideCount = slides.length;
  const currentSlide = useMemo(() => slides[currentIndex], [currentIndex]);
  const calloutCopy = useMemo(() => {
    return buildCalloutCopy(currentSlide);
  }, [currentSlide.callouts, currentSlide.details, currentSlide.subtitle]);
  const detailList = useMemo(() => {
    return buildDetailList(currentSlide, calloutCopy);
  }, [currentSlide.details, calloutCopy]);
  const tileIcons = [FaMusic, FaBook, FaGlobe, FaBullhorn, FaLightbulb, FaUsers, FaVideo, FaCalendarAlt, FaChartPie];

  useEffect(() => {
    if (!isPlaying) return undefined;
    const ticker = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % slideCount);
    }, 6000);
    return () => clearInterval(ticker);
  }, [isPlaying, slideCount]);

  const goToPrevious = useCallback(() => {
    setCurrentIndex((prevIndex) => (prevIndex - 1 + slideCount) % slideCount);
    setIsPlaying(false);
  }, [slideCount]);

  const goToNext = useCallback(() => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % slideCount);
    setIsPlaying(false);
  }, [slideCount]);

  const togglePlay = useCallback(() => {
    setIsPlaying((prev) => !prev);
  }, []);

  const toggleControls = useCallback(() => {
    setControlsVisible((prev) => !prev);
  }, []);

  const exportSlidesToPdf = useCallback(async () => {
    if (isExporting) return;
    const exportRoot = exportRootRef.current;
    if (!exportRoot) return;
    const exportSlides = Array.from(exportRoot.querySelectorAll(".help-export-slide"));
    if (!exportSlides.length) return;

    setIsExporting(true);
    try {
      const pdf = new jsPDF({
        orientation: "landscape",
        unit: "px",
        format: [EXPORT_WIDTH, EXPORT_HEIGHT],
      });

      for (let index = 0; index < exportSlides.length; index += 1) {
        const slideNode = exportSlides[index];
        const canvas = await html2canvas(slideNode, {
          backgroundColor: "#030303",
          scale: 2,
          useCORS: true,
        });
        const imgData = canvas.toDataURL("image/png");
        if (index > 0) {
          pdf.addPage([EXPORT_WIDTH, EXPORT_HEIGHT], "landscape");
        }
        pdf.addImage(imgData, "PNG", 0, 0, EXPORT_WIDTH, EXPORT_HEIGHT);
      }

      pdf.save("gcc-help-slides.pdf");
    } finally {
      setIsExporting(false);
    }
  }, [isExporting]);

  const renderSlide = (slide, index, total, options = {}) => {
    const slideCallouts = options.calloutCopy ?? buildCalloutCopy(slide);
    const slideDetails = options.detailList ?? buildDetailList(slide, slideCallouts);
    const SlideVisualIcon = slide.icon;
    return (
      <section
        className={`slide-card ${slide.videoOnly ? "slide-card-video-only" : ""} ${options.className || ""}`.trim()}
        style={{
          borderColor: slide.accentColor,
          boxShadow: `0 0 40px ${slide.accentColor}44`,
        }}
        aria-live={options.ariaLive || undefined}
      >
        {!slide.videoOnly && (
          <div
            className="slide-visual"
            style={{ borderColor: slide.accentColor, background: `linear-gradient(135deg, ${slide.accentColor}1a, transparent 60%)` }}
          >
            <div className="slide-visual-icon" style={{ color: slide.accentColor }}>
              {SlideVisualIcon && <SlideVisualIcon aria-hidden="true" />}
            </div>
            <span className="slide-visual-label">{slide.accent}</span>
          </div>
        )}
        <div className="slide-main">
          {!slide.videoOnly && (
            <div className="slide-header">
              <h1 className="slide-title">{slide.title}</h1>
            </div>
          )}
          {slide.videoUrl && (
            <div className="slide-video">
              <iframe
                src={slide.videoUrl}
                title={slide.title ?? "Help video"}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          )}
          {!slide.videoOnly && slideCallouts.length > 0 && (
            <div className="slide-main-callout">
              {slideCallouts.map((line) => (
                <p key={line}>
                  <FaCheck aria-hidden="true" />
                  {line}
                </p>
              ))}
            </div>
          )}
          {!slide.videoOnly && slide.description && (
            <p className="slide-description">{slide.description}</p>
          )}
          {!slide.videoOnly && slideDetails.length > 0 && (
            <div className="slide-detail-grid">
              {slideDetails.map((detail, tileIndex) => {
                const TileIcon = tileIcons[tileIndex % tileIcons.length];
                return (
                  <article className="slide-detail-tile" key={detail}>
                    <span className="slide-detail-icon" aria-hidden="true">
                      <TileIcon />
                    </span>
                    <p>{detail}</p>
                  </article>
                );
              })}
            </div>
          )}
          {!slide.videoOnly && slide.shortcuts?.length > 0 && (
            <div className="slide-shortcuts">
              <span className="slide-shortcuts-notice">
                Hold <strong>Ctrl</strong> (Windows) or <strong>⌘</strong> (Mac) + the key below to jump modules instantly.
              </span>
              <div className="slide-shortcuts-grid">
                {slide.shortcuts.map((shortcut) => (
                  <div className="slide-shortcut-item" key={shortcut.label}>
                    <span className="slide-shortcut-key">Ctrl + {shortcut.key}</span>
                    <span className="slide-shortcut-label">{shortcut.label}</span>
                    <span className="slide-shortcut-target">
                      {shortcut.external ? "GCC Worship YouTube" : shortcut.target}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {!slide.videoOnly && slide.pageShortcutGroups?.length > 0 && (
            <div className="slide-shortcuts slide-page-shortcuts">
              <span className="slide-shortcuts-notice">Page-specific shortcuts</span>
              <div className="slide-page-shortcuts-grid">
                {[...slide.pageShortcutGroups]
                  .sort((a, b) => b.shortcuts.length - a.shortcuts.length)
                  .map((group) => (
                  <div className="slide-page-shortcut-group" key={group.page}>
                    <span className="slide-page-shortcut-title">{group.page}</span>
                    <div className="slide-page-shortcut-list">
                      {group.shortcuts.map((shortcut) => (
                        <div className="slide-page-shortcut" key={`${group.page}-${shortcut.keys}-${shortcut.description}`}>
                          <span className="slide-page-shortcut-keys">{shortcut.keys}</span>
                          <span className="slide-page-shortcut-desc">{shortcut.description}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {!slide.videoOnly && slide.hashtags?.length > 0 && (
            <div className="slide-hashtags">
              {slide.hashtags.map((tag) => (
                <span key={tag}>{tag}</span>
              ))}
            </div>
          )}
          {!slide.videoOnly && slide.links?.length > 0 && (
            <div className="slide-links">
              {slide.links.map((link) => (
                <a key={link.label} href={link.url} target="_blank" rel="noreferrer">
                  {link.label}
                </a>
              ))}
            </div>
          )}
        </div>
        <div className="slide-footer">
          <span className="slide-progress">
            Slide {index + 1} / {total}
          </span>
        </div>
      </section>
    );
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.code === "ArrowLeft") {
        event.preventDefault();
        goToPrevious();
      }
      if (event.code === "ArrowRight") {
        event.preventDefault();
        goToNext();
      }
      if (event.code === "Space") {
        event.preventDefault();
        togglePlay();
      }
      if (event.key === "Control" && !event.repeat) {
        toggleControls();
      }
      if (event.code === "Escape") {
        toggleControls();
      }
    };
    const handleWindowBlur = () => setControlsVisible(true);

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("blur", handleWindowBlur);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("blur", handleWindowBlur);
    };
  }, [goToNext, goToPrevious, togglePlay, toggleControls]);

  return (
    <main className="help-slideshow">
      <SEO
        title="Help"
        description="Help carousel with autoplay slides and controls."
      />
      {renderSlide(currentSlide, currentIndex, slideCount, {
        calloutCopy,
        detailList,
        ariaLive: "polite",
      })}

      <div className={`control-panel ${controlsVisible ? "" : "hidden-controls"}`}>
        <button
          type="button"
          className="control-button"
          onClick={goToPrevious}
          aria-label="Previous slide"
        >
          <FaChevronLeft />
        </button>
        <button type="button" className="control-button" onClick={togglePlay} aria-label="Play">
          {isPlaying ? <FaPause /> : <FaPlay />}
        </button>
        <button
          type="button"
          className="control-button"
          onClick={goToNext}
          aria-label="Next slide"
        >
          <FaChevronRight />
        </button>
        <button
          type="button"
          className="control-button"
          onClick={exportSlidesToPdf}
          aria-label="Export help slides as PDF"
          title="Export slides to PDF"
          disabled={isExporting}
        >
          <FaFilePdf />
        </button>
      </div>

      <div className={`progress-dots ${controlsVisible ? "" : "hidden-controls"}`}>
        {slides.map((slide, index) => (
          <button
            key={slide.id}
            type="button"
            className={`progress-dot ${index === currentIndex ? "active" : ""}`}
            onClick={() => {
              setCurrentIndex(index);
              setIsPlaying(false);
            }}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>

      <div className="help-export-layer" aria-hidden="true" ref={exportRootRef}>
        {slides.map((slide, index) => (
          <div className="help-slideshow help-export-shell help-export-slide" key={`export-${slide.id}`}>
            {renderSlide(slide, index, slideCount)}
          </div>
        ))}
      </div>
    </main>
  );
}

export default Help;
